import angular from 'angular'
import { ServerEnvironment } from '../../../components/server-environment'
import {
  LogLevel,
  StacktraceCaptureMode
} from '../../../components/servers-grid/shared'
import { legacyMakeCancellableRequest } from '../promise-service'
import { SyslogSeverity, SyslogProtocol } from '../../../components/syslog'
import { ServerCleanupPolicyTimeUnit } from './legacy-server-settings-controller'
type RequestNoArgs = () => angular.IHttpPromise<unknown>
type RequestByServerId = (serverId: string) => angular.IHttpPromise<unknown>
type RequestCancellable = angular.IHttpPromise<unknown> & {
  readonly _httpTimeout: angular.IDeferred<unknown>
}

export interface LegacyServerFactoryGetSettingsResponse
  extends SyslogApiSettings {
  readonly name: string
  readonly assess: boolean
  readonly defend: boolean
  readonly environment: ServerEnvironment
  readonly bot_protection: boolean
  readonly log_level: LogLevel
  readonly log_path: null | string
  readonly sampling: boolean
  readonly stacktrace_capture_mode: StacktraceCaptureMode
  readonly sampling_baseline: number
  readonly sampling_frequency: number
  readonly sampling_window: number
  readonly syslog_enabled: boolean
  readonly licensed: boolean
}

export interface LegacyServerFactorySaveSettingsData {
  readonly name: string
  readonly assess: boolean
  readonly log_level: LogLevel
  readonly environment: ServerEnvironment
  readonly log_path?: string
  readonly sampling: boolean
  readonly stacktrace_capture_mode: StacktraceCaptureMode
  readonly sampling_baseline: number
  readonly sampling_frequency: number
  readonly sampling_window: number
}

export interface ServerCleanupPolicy {
  readonly timeframe: number
  unit: ServerCleanupPolicyTimeUnit
  readonly environment: ServerEnvironment
}

export interface GetOrgSettingsBaseResp {
  readonly assess: boolean
  readonly defend: boolean
  readonly environment: ServerEnvironment
  readonly bot_protection: boolean
  readonly log_level: LogLevel
  readonly sampling: boolean
  readonly stacktrace_capture_mode: StacktraceCaptureMode
  readonly sampling_baseline: number
  readonly sampling_frequency: number
  readonly sampling_window: number
  readonly syslog_enabled: boolean
  readonly server_cleanup_policy: ServerCleanupPolicy
  readonly default_environment: ServerEnvironment
  readonly auto_license_protection: boolean
  readonly telemetry_enabled: boolean
  readonly retain_library_data: boolean
  readonly server_cleanup_enhanced: boolean
}

export type GetOrgSettingsResp = GetOrgSettingsBaseResp & SyslogApiSettings

export interface SyslogApiSettings {
  readonly syslog_ip_address: undefined | null | string
  readonly syslog_port_number: undefined | null | number
  readonly syslog_facility_code: undefined | null | number
  readonly syslog_protocol: undefined | null | SyslogProtocol
  readonly syslog_severity_exploited: undefined | null | SyslogSeverity
  readonly syslog_severity_suspicious: undefined | null | SyslogSeverity
  readonly syslog_severity_blocked: undefined | null | SyslogSeverity
  readonly syslog_severity_blocked_perimeter: undefined | null | SyslogSeverity
  readonly syslog_severity_probed: undefined | null | SyslogSeverity
}

export interface LegacyServerFactory {
  readonly getOrgSettingsForEnvironment: (
    environment: ServerEnvironment
  ) => angular.IHttpPromise<{
    readonly settings: GetOrgSettingsBaseResp & SyslogApiSettings
  }>
  readonly saveDefenseSettings: (
    serverId: string,
    settings: {
      readonly defend: boolean
      readonly bot_protection: boolean
      readonly syslog_enabled: boolean
    } & SyslogApiSettings
  ) => angular.IHttpPromise<unknown>
  readonly saveSettings: (
    serverId: string,
    settings: LegacyServerFactorySaveSettingsData
  ) => angular.IHttpPromise<{
    readonly messages: string[]
  }>
  readonly getFiltersCatalogs: RequestNoArgs
  readonly getFiltersByType: (type, filters) => RequestCancellable
  readonly getServers: any
  readonly getServersBase: RequestNoArgs
  readonly hasActiveServer: () => angular.IHttpPromise<unknown>
  readonly getServer: RequestByServerId
  readonly getServerProperties: RequestByServerId
  readonly deleteServer: (
    serverId: string,
    deletetraces
  ) => angular.IHttpPromise<unknown>
  readonly getAppTraces: RequestByServerId
  readonly getSettings: (serverId: string) => angular.IHttpPromise<
    LegacyTeamServerApiResponse & {
      readonly settings: LegacyServerFactoryGetSettingsResponse
    }
  >
  readonly toggleAssess: RequestByServerId
  readonly toggleDefend: RequestByServerId
  readonly getLibStats: RequestByServerId
  readonly getVulnSeverityBreakdown: RequestByServerId
  readonly getVulnStatusBreakdown: RequestByServerId
  readonly getVulnRuleBreakdown: RequestByServerId
  readonly getAttackStatusBreakdown: RequestByServerId
  readonly getAttackTypeBreakdown: RequestByServerId
  readonly getServerActivityByInterval: (
    serverId: string,
    interval
  ) => angular.IHttpPromise<unknown>
  readonly updateName: (
    serverId: string,
    displayName
  ) => angular.IHttpPromise<unknown>
  readonly getLibrariesStats: RequestByServerId
  readonly getLibrariesFiltersByType: (
    type,
    serverId: string,
    filters
  ) => RequestCancellable
  readonly getLibrariesScoreBreakdown: RequestByServerId
  readonly getLibrariesOutOfDate: RequestByServerId
  readonly getServerLibrariesByFilter: (data, serverId) => RequestCancellable
  readonly getLibraries: (
    serverId: string,
    expandValue
  ) => angular.IHttpPromise<unknown>
  readonly getAttacks: RequestByServerId
  readonly getServersQuickFilters: (filters) => RequestCancellable
}

// Note: technically this should be named "Server Service Factory", since it is a factory function that
// returns an AngularJS service, but since this is legacy code, I don't want to make name changes if we can avoid it.
export function ServerFactory(
  $http: {
    <T>(config: angular.IRequestConfig): angular.IHttpPromise<T>
    readonly get: angular.IHttpService['get']
    readonly put: angular.IHttpService['put']
    readonly delete: angular.IHttpService['delete']
  },
  $q: {
    readonly defer: angular.IQService['defer']
  },
  UserService: {
    readonly buildAPIUrl: LegacyUserService['buildAPIUrl']
  }
): LegacyServerFactory {
  'ngInject'

  return {
    getFiltersCatalogs: () => {
      return $http.get(UserService.buildAPIUrl('/servers/filters/listing'))
    },
    getFiltersByType: (type, filters) =>
      legacyMakeCancellableRequest($q, $http, {
        method: 'get',
        url: UserService.buildAPIUrl('/servers/filters/' + type + '/listing'),
        params: filters
      }),
    getServers: (includeArchived, expandParam) => {
      return $http.get(UserService.buildAPIUrl('/servers'), {
        params: {
          includeArchived,
          expand: expandParam
        }
      })
    },
    getServersBase: () => {
      return $http.get(UserService.buildAPIUrl('/servers'), {
        params: { base: true }
      })
    },
    hasActiveServer: () => {
      return $http.get(UserService.buildAPIUrl('/servers/active'))
    },

    getServer: (serverId) => {
      return $http.get(UserService.buildAPIUrl('/servers/' + serverId), {
        params: {
          expand: 'applications,server_license,assess_protect_status_locked'
        }
      })
    },
    getServerProperties: (serverId) => {
      return $http.get(
        UserService.buildAPIUrl('/servers/' + serverId + '/properties')
      )
    },
    deleteServer: (serverId, deletetraces) => {
      return $http.delete(UserService.buildAPIUrl('/servers/' + serverId), {
        params: { deletetraces }
      })
    },

    getAppTraces: (serverId) => {
      return $http.get(
        UserService.buildAPIUrl('/servers/' + serverId + '/apptraces'),
        { params: { orphans: true } }
      )
    },

    getOrgSettingsForEnvironment: (environment) => {
      return $http.get(
        UserService.buildAPIUrl('/server/settings/' + environment)
      )
    },

    // Server Level Settings
    getSettings: (serverId) => {
      return $http.get(
        UserService.buildAPIUrl('/servers/' + serverId + '/edit')
      )
    },
    saveSettings: (serverId, settings) => {
      return $http.put(
        UserService.buildAPIUrl('/servers/' + serverId + '/edit'),
        settings
      )
    },
    saveDefenseSettings: (serverId, settings) => {
      return $http.put(
        UserService.buildAPIUrl('/servers/' + serverId + '/edit/defense'),
        settings
      )
    },
    toggleAssess: (serverId) => {
      return $http.put(
        UserService.buildAPIUrl('/servers/' + serverId + '/assess'),
        {}
      )
    },
    toggleDefend: (serverId) => {
      return $http.put(
        UserService.buildAPIUrl('/servers/' + serverId + '/defend'),
        {}
      )
    },

    getLibStats: (serverId) => {
      return $http.get(
        UserService.buildAPIUrl('/servers/' + serverId + '/libraries/breakdown')
      )
    },
    getVulnSeverityBreakdown: (serverId) => {
      return $http.get(
        UserService.buildAPIUrl(
          '/servers/' + serverId + '/breakdown/trace/severity'
        )
      )
    },
    getVulnStatusBreakdown: (serverId) => {
      return $http.get(
        UserService.buildAPIUrl(
          '/servers/' + serverId + '/breakdown/trace/status'
        )
      )
    },
    getVulnRuleBreakdown: (serverId) => {
      return $http.get(
        UserService.buildAPIUrl(
          '/servers/' + serverId + '/breakdown/trace/rule'
        )
      )
    },
    getAttackStatusBreakdown: (serverId) => {
      return $http.get(
        UserService.buildAPIUrl(
          '/servers/' + serverId + '/breakdown/attack/status'
        )
      )
    },
    getAttackTypeBreakdown: (serverId) => {
      return $http.get(
        UserService.buildAPIUrl(
          '/servers/' + serverId + '/breakdown/attack/type'
        )
      )
    },
    getServerActivityByInterval: (serverId, interval) => {
      return $http.get(
        UserService.buildAPIUrl('/servers/' + serverId + '/activity/interval'),
        { params: { interval } }
      )
    },
    updateName: (serverId, displayName) => {
      return $http.put(
        UserService.buildAPIUrl('/servers/' + serverId + '/name'),
        { name: displayName }
      )
    },
    // Library Tab
    getLibrariesStats: (serverId) => {
      return $http.get(
        UserService.buildAPIUrl('/servers/' + serverId + '/libraries/stats')
      )
    },
    getLibrariesFiltersByType: (type, serverId, filters) =>
      legacyMakeCancellableRequest($q, $http, {
        method: 'get',
        url: UserService.buildAPIUrl(
          '/servers/' + serverId + '/libraries/filters/' + type + '/listing'
        ),
        params: filters
      }),
    getLibrariesScoreBreakdown: (serverId) => {
      return $http.get(
        UserService.buildAPIUrl(
          '/servers/' + serverId + '/libraries/breakdown/scores'
        )
      )
    },
    getLibrariesOutOfDate: (serverId) => {
      return $http.get(
        UserService.buildAPIUrl(
          '/servers/' + serverId + '/libraries/breakdown/outofdate'
        )
      )
    },
    getServerLibrariesByFilter: (data, serverId) =>
      legacyMakeCancellableRequest($q, $http, {
        method: 'get',
        url: UserService.buildAPIUrl(
          '/servers/' + serverId + '/libraries/filter'
        ),
        params: data
      }),
    getLibraries: (serverId, expandValue) => {
      return $http.get(
        UserService.buildAPIUrl('/servers/' + serverId + '/libraries'),
        { params: { expand: expandValue } }
      )
    },
    getAttacks: (serverId) => {
      return $http.get(
        UserService.buildAPIUrl('/servers/' + serverId + '/attacks')
      )
    },
    getServersQuickFilters: (filters) =>
      legacyMakeCancellableRequest($q, $http, {
        method: 'get',
        url: UserService.buildAPIUrl('/servers/filters/quick'),
        params: filters
      })
  }
}
